import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, ShieldCheck, Target, Users, BadgeCheck } from "lucide-react";

const OFFICE =
  "https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBib2FyZHJvb218ZW58MHx8fHwxNzgzMDkyMTQ5fDA&ixlib=rb-4.1.0&q=85";

const Overline = ({ children }) => (
  <span className="inline-block font-sans text-[11px] md:text-xs uppercase tracking-[0.28em] text-[#C5A880] font-semibold">
    {children}
  </span>
);

const PRINCIPLES = [
  {
    icon: Target,
    title: "Precision Execution",
    body: "Every reconciliation, model, and report is engineered to be defensible and clear.",
  },
  {
    icon: Compass,
    title: "Strategic Insight",
    body: "We connect the numbers to the decision — never the other way around.",
  },
  {
    icon: ShieldCheck,
    title: "Discretion & Trust",
    body: "Your financials, plans, and cap tables never leave a locked room.",
  },
  {
    icon: Users,
    title: "Founder-First",
    body: "Senior operators embedded in your team — no junior handoffs, no wasted cycles.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <Overline>About Ervion</Overline>
            <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium text-[#0A192F] leading-[0.98]">
              Your trusted partner in <br />
              <span className="italic">financial management.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="font-sans text-lg text-slate-600 leading-relaxed">
              Ervion provides accounting and financial advisory services designed to help startups
              and growing businesses build stronger financial operations. Led by a U.S.-licensed
              Certified Public Accountant (CPA), we deliver expert bookkeeping, financial reporting,
              FP&A, fractional CFO services, and strategic financial guidance that grows with your
              business.
            </p>
            <div className="mt-10 inline-flex items-center gap-3 border border-[#C5A880] px-5 py-3">
              <BadgeCheck size={18} strokeWidth={1.5} className="text-[#C5A880]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Licensed</div>
                <div className="font-serif text-lg text-[#0A192F] tracking-tight">CPA 0024525</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <img src={OFFICE} alt="Modern boardroom" className="w-full h-[420px] md:h-[520px] object-cover hero-image-mask" />
      </section>

      {/* MISSION */}
      <section className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Overline>Mission</Overline>
          </div>
          <div className="lg:col-span-8">
            <p className="font-serif text-3xl md:text-4xl text-[#0A192F] leading-[1.25] tracking-tight">
              Our mission is to help businesses thrive by providing reliable accounting, strategic
              financial guidance, and exceptional service. We simplify financial operations so
              business owners can focus on growth with <span className="italic text-[#C5A880]">clarity and confidence.</span>
            </p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Overline>Our Approach</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tighter font-medium text-[#0A192F] leading-[1.05]">
              We give business owners the financial clarity to make <span className="italic">confident decisions.</span>
            </h2>
            <p className="mt-8 font-sans text-slate-600 leading-relaxed">
              Ervion combines CPA-led expertise with modern technology to deliver accurate
              accounting, reliable financial reporting, and strategic advisory services. We build
              scalable financial operations that help businesses stay organized, improve
              performance, and grow with confidence.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 border-l border-t border-[#E2E8F0]">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="border-r border-b border-[#E2E8F0] p-8 md:p-10">
                  <Icon size={26} strokeWidth={1.5} className="text-[#0A192F]" />
                  <h3 className="mt-6 font-serif text-2xl text-[#0A192F] tracking-tight">{p.title}</h3>
                  <p className="mt-3 font-sans text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Overline>Work With Us</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Let&apos;s build a stronger <span className="italic text-[#C5A880]">financial future</span> for your business.
            </h2>
            <p className="mt-6 font-sans text-slate-300 leading-relaxed max-w-xl">
              Whether you&apos;re launching, growing, or scaling, we&apos;re here to provide the financial
              expertise your business needs.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center gap-3 bg-[#C5A880] text-[#0A192F] px-8 py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-white transition-all hover:-translate-y-0.5"
            >
              Schedule a Consultation <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
